import { Show, createSignal, onCleanup, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import type { UpstreamState } from "./api";
import app from "../../App.module.css";
import { clearUpstreamCache } from "../../shared/api/endpoints";
import { TextTooltip } from "../../shared/inputs/TextTooltip";
import s from "./UpstreamMenu.module.css";

interface Props {
  state: UpstreamState;
  tokenExpiresAt: string | null;
  // Live entry count pushed via /api/events WS (cache-count messages).
  // null until the first message lands (or when the WS is down).
  cacheCount: number | null;
  viewerEmail: string;
  onReauth(): void;
  onRefreshToken(): void;
  onLogout(): void;
}

type Severity = "ok" | "warn" | "error" | "disabled";

function formatRemaining(ms: number): string {
  if (ms <= 0) return "expired";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(Math.abs(ms) / 1000);
  const m = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function tokenSeverity(expiresAt: string | null, now: number): Severity {
  if (!expiresAt) return "error";
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return "error";
  if (ms < 5 * 60 * 1000) return "warn";
  return "ok";
}

function sessionSeverity(state: UpstreamState): Severity {
  switch (state.kind) {
    case "disabled": return "disabled";
    case "healthy": return "ok";
    case "reauth-in-progress": return "error";
    case "reauth-needed": return "error";
  }
}

function aggregateSeverity(token: Severity, session: Severity): Severity {
  if (session === "disabled") return "disabled";
  if (token === session) return token;
  // Mixed signals blend to amber — e.g. valid token but no session yet.
  return "warn";
}

function dotClass(sev: Severity): string {
  switch (sev) {
    case "ok": return s.dotOk ?? "";
    case "warn": return s.dotWarn ?? "";
    case "error": return s.dotError ?? "";
    case "disabled": return s.dotDisabled ?? "";
  }
}

function sevColor(sev: Severity): string {
  switch (sev) {
    case "ok": return "#10b981";
    case "warn": return "#f59e0b";
    case "error": return "#ef4444";
    case "disabled": return "#94a3b8";
  }
}

function tokenValidity(expiresAt: string | null, now: number): string {
  if (!expiresAt) return "None";
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return "Expired";
  return formatRemaining(ms);
}

function sessionValidity(state: UpstreamState, now: number): string {
  switch (state.kind) {
    // "Disabled" + the kind in parens used to live here. The action
    // button's tooltip carries the reason now — keeps the cell tidy
    // while preserving the why on hover.
    case "disabled":
    case "reauth-in-progress":
    case "reauth-needed":
      return "None";
    case "healthy": return formatElapsed(now - new Date(state.healthySinceAt).getTime());
  }
}

export function UpstreamMenu(props: Props): JSX.Element {
  const [open, setOpen] = createSignal(false);
  let triggerEl: HTMLButtonElement | undefined;
  let menuEl: HTMLDivElement | undefined;
  const [menuPos, setMenuPos] = createSignal<{ top: number; right: number } | null>(null);

  // 1s ticker so validity columns count down in real time.
  const [now, setNow] = createSignal(Date.now());
  const handle = setInterval(() => setNow(Date.now()), 1_000);
  onCleanup(() => clearInterval(handle));

  function reposition() {
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
  }
  function toggle() {
    if (open()) { setOpen(false); return; }
    reposition();
    setOpen(true);
  }
  // Blur the trigger on every close path (Escape, click-outside) so
  // the pill doesn't keep a focus ring after the menu hides.
  function close() { setOpen(false); triggerEl?.blur(); }

  function onDocClick(e: MouseEvent) {
    if (!open()) return;
    const t = e.target as Node;
    if (triggerEl?.contains(t)) return;
    if (menuEl?.contains(t)) return;
    close();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape" && open()) close();
  }
  function onWinChange() { if (open()) reposition(); }

  // Capture phase + pointerdown + mousedown, matching SettingsMenu —
  // vis-timeline's Hammer.js swallows bubble-phase mousedown on
  // anything overlapping the timeline.
  document.addEventListener("pointerdown", onDocClick, true);
  document.addEventListener("mousedown", onDocClick, true);
  document.addEventListener("keydown", onKey);
  window.addEventListener("resize", onWinChange);
  window.addEventListener("scroll", onWinChange, true);
  onCleanup(() => {
    document.removeEventListener("pointerdown", onDocClick, true);
    document.removeEventListener("mousedown", onDocClick, true);
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onWinChange);
    window.removeEventListener("scroll", onWinChange, true);
  });

  const tokenSev = () => tokenSeverity(props.tokenExpiresAt, now());
  const sessionSev = () => sessionSeverity(props.state);
  const aggSev = () => aggregateSeverity(tokenSev(), sessionSev());

  const sessionActionEnabled = () => props.state.kind !== "disabled";
  const isHealthy = () => props.state.kind === "healthy";

  // Cache count is pushed from the /api/events WS — no local polling.
  // The Clear button only fires the DELETE; the backend then broadcasts
  // a `cache-cleared` message which every connected client's WS handler
  // turns into a window.location.reload() (so all dashboards refresh).
  const [cacheBusy, setCacheBusy] = createSignal(false);
  async function onClearCache(): Promise<void> {
    setCacheBusy(true);
    try {
      await clearUpstreamCache();
      // No local reload — the broadcast from the backend triggers it
      // for every connected client (including this one) so the code
      // path is the same regardless of who clicked Clear.
    } finally {
      setCacheBusy(false);
    }
  }

  const triggerProperties = () => [
    { label: "Token", value: tokenValidity(props.tokenExpiresAt, now()) },
    { label: "Session", value: sessionValidity(props.state, now()) },
  ];

  return (
    <>
      <TextTooltip
        text="Remote Authentication"
        stripe={sevColor(aggSev())}
        disabled={open()}
        properties={triggerProperties()}
      >
        <span class={s.pillWrap}>
          <button
            ref={(el) => (triggerEl = el)}
            type="button"
            class={`${app.userPill} ${open() ? app.userPillOpen : ""}`}
            aria-haspopup="menu"
            aria-expanded={open()}
            aria-label={`Remote Authentication — ${triggerProperties().map((p) => `${p.label}: ${p.value}`).join("; ")}`}
            onClick={toggle}
          >
            <svg class={app.userIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="8" cy="15" r="4" />
              <path d="M10.85 12.15 19 4" />
              <path d="M18 5l2 2" />
              <path d="M15 8l2 2" />
            </svg>
          </button>
          <span class={`${s.stateDot} ${dotClass(aggSev())}`} aria-hidden="true" />
        </span>
      </TextTooltip>
      <Show when={open() && menuPos()}>
        {(pos) => (
          <Portal>
            <div
              ref={(el) => (menuEl = el)}
              class={app.userMenu}
              role="menu"
              style={{ top: `${pos().top}px`, right: `${pos().right}px` }}
            >
              <header class={app.userMenuHeader}>
                <div class={app.userMenuEmail}>Remote Authentication</div>
              </header>
              <div class={s.grid}>
                <div class={s.headerCell}>Status</div>
                <div class={s.headerCell}>Kind</div>
                <div class={s.headerCell}>Lifetime</div>
                <div class={s.headerCell}>Action</div>

                <span class={`${s.cellDot} ${dotClass(tokenSev())}`} aria-hidden="true" />
                <span class={s.cellKind}>Token</span>
                <span class={s.cellValidity}>{tokenValidity(props.tokenExpiresAt, now())}</span>
                <TextTooltip
                  text="Refresh"
                  description={isHealthy()
                    ? "Trigger a one-shot silent refresh from the backend orchestrator"
                    : "No active session — authenticate first"}
                >
                <button
                  type="button"
                  role="menuitem"
                  class={s.cellAction}
                  disabled={!isHealthy()}
                  onClick={() => { close(); props.onRefreshToken(); }}
                >
                  Refresh
                </button>
                </TextTooltip>

                <span class={`${s.cellDot} ${dotClass(sessionSev())}`} aria-hidden="true" />
                <span class={s.cellKind}>Session</span>
                <span class={s.cellValidity}>{sessionValidity(props.state, now())}</span>
                <TextTooltip
                  text={isHealthy() ? "Logout" : "Authenticate"}
                  description={
                    isHealthy()
                      ? "Wipe the local token and force a re-authentication on next use"
                      : sessionActionEnabled()
                      ? "Open the Playwright re-auth window"
                      : `Upstream auth is disabled${props.state.kind === "disabled" ? ` (${props.state.reason})` : ""}`
                  }
                  stripe={isHealthy() ? "#ef4444" : undefined}
                >
                <button
                  type="button"
                  role="menuitem"
                  class={`${s.cellAction} ${isHealthy() ? s.cellActionDanger : ""}`}
                  disabled={!sessionActionEnabled()}
                  onClick={() => {
                    close();
                    if (isHealthy()) props.onLogout();
                    else props.onReauth();
                  }}
                >
                  {isHealthy() ? "Logout" : "Authenticate"}
                </button>
                </TextTooltip>

                <span class={`${s.cellDot} ${dotClass("ok")}`} aria-hidden="true" />
                <span class={s.cellKind}>Cache</span>
                <span class={s.cellValidity}>
                  {props.cacheCount === null ? "—" : `${props.cacheCount} entr${props.cacheCount === 1 ? "y" : "ies"}`}
                </span>
                <TextTooltip
                  text="Clear cache"
                  description="Wipe every cached upstream response — all connected dashboards reload to fetch fresh data"
                  stripe="#ef4444"
                >
                <button
                  type="button"
                  role="menuitem"
                  class={`${s.cellAction} ${s.cellActionDanger}`}
                  disabled={cacheBusy() || (props.cacheCount ?? 0) === 0}
                  onClick={() => { void onClearCache(); }}
                >
                  {cacheBusy() ? "Clearing…" : "Clear"}
                </button>
                </TextTooltip>
              </div>
            </div>
          </Portal>
        )}
      </Show>
    </>
  );
}
